import { useGetFormalOffers } from '@api/lendingAPI/lapiHooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    GetFormalOffersResponse,
    GetIwocapayPaymentLaterResponse
} from '@iwoca/lapi-client/edge';
import { Button, Skeleton } from '@iwoca/orion';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import styles from './OfferChoiceForm.module.css';
import { offerChoiceValidator } from './OfferChoiceValidator';
import { Radio } from './RadioInput/RadioInput';
import { useDrawDownRepaymentScheduleIwocaPay } from './useDrawDownRepaymentScheduleIwocaPay';
import { filterAvailableDurationsByPayLink } from './utils/filterAvailableDurationsByPaylink';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { getEcomOrderIdFromQueryParams } from '../../../../myAccount/drawDown/DrawDownHelper';
import { displayCurrency } from '../ANTCard/ANTCard';
import { SecurityChecksPrompt } from '../approvedView/SecurityChecksPrompt/SecurityChecksPrompt';
import { useTrackViewedOffers } from '../hooks/useTrackViewedOffers';
import {
    is30DaysOffer,
    mapOfferDurationToMonths
} from '../mapOfferDurationToMonths';
import { useGetSecurityCheckRequirements } from '../SLSecurityCheckBanner/SLSecurityCheckBanner';

type FormValues = {
    offerId: string;
};

export const OfferChoiceForm = ({
    payLink
}: {
    payLink: GetIwocapayPaymentLaterResponse['data'][number];
}) => {
    const { isAnyRequirementVisible } = useGetSecurityCheckRequirements();
    const stateKey = useStateKey();
    const { formalOffers, loadingFormalOffers } = useGetFormalOffers();
    const orderId = getEcomOrderIdFromQueryParams();

    const initialFormValues: FormValues = {
        offerId: ''
    };
    const methods = useForm({
        defaultValues: initialFormValues,
        resolver: yupResolver(offerChoiceValidator),
        mode: 'all'
    });
    const handleSubmit = ({ offerId }: FormValues) => {
        // Copied from the saga. Will change as this feels bit confusing.
        const targetURL = new URL(
            `${window.location.protocol}//${window.location.host}/pay/checkout/`
        );

        targetURL.searchParams.append('payLinkId', payLink.id);
        targetURL.searchParams.append('offerId', offerId);
        if (orderId) {
            targetURL.searchParams.append('orderId', orderId);
        }

        targetURL.searchParams.append('state_key', stateKey);

        tracking.ampli.selectedOffer({
            pay_link_id: payLink.id,
            offer_ids: formalOffers.map((offer) => offer.offer_id!),
            offer_id: offerId
        });

        const overlay = new URLSearchParams(window.location.search).get(
            'overlay'
        );
        if (overlay) {
            targetURL.searchParams.append('overlay', overlay);
        }

        window.location.href = targetURL.toString();
        return;
    };
    const onSubmit = methods.handleSubmit(handleSubmit);
    const { isValid } = methods.formState;

    useTrackViewedOffers({
        payLinkId: payLink.id,
        formalOffers: formalOffers,
        loadingApis: loadingFormalOffers
    });

    if (loadingFormalOffers)
        return (
            <>
                <Skeleton />
                <Skeleton />
                <Skeleton />
            </>
        );

    if (!formalOffers) return null;

    const availableProducts = filterAvailableDurationsByPayLink({
        offers: formalOffers,
        payLink
    });

    if (availableProducts.length === 1) {
        const selectedOption = methods.getValues(PAYMENT_OPTION_INPUT_ID);
        if (!selectedOption) {
            methods.setValue(
                PAYMENT_OPTION_INPUT_ID,
                availableProducts[0].offer_id!
            );
        }
    }

    return (
        <div>
            <FormProvider {...methods}>
                <div className={styles.subtitle}>Choose a payment option</div>
                <form
                    autoComplete="off"
                    className={styles.form}
                    onSubmit={onSubmit}
                >
                    <div className={styles.options}>
                        {availableProducts.map((offer) => (
                            <OfferChoiceOption
                                key={offer.offer_id}
                                offer={offer}
                                payLink={payLink}
                            />
                        ))}
                    </div>
                    <SecurityChecksPrompt />
                    <div className="flex justify-end">
                        <Button
                            className="max-xs:w-full"
                            aria-disabled={!isValid}
                            disabled={!isValid}
                        >
                            {isAnyRequirementVisible
                                ? 'Complete security checks'
                                : 'Finish checkout'}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </div>
    );
};

const PAYMENT_OPTION_INPUT_ID = 'offerId';
const OfferChoiceOption = ({
    offer,
    payLink
}: {
    offer: NonNullable<
        GetFormalOffersResponse['data']
    >['formal_offers'][number];
    payLink: GetIwocapayPaymentLaterResponse['data'][number];
}) => {
    const { register, watch } = useFormContext();
    const watchPaymentOption = watch(PAYMENT_OPTION_INPUT_ID);
    const isChecked = watchPaymentOption === offer.offer_id!;
    const id = offer.offer_id!;
    const duration = mapOfferDurationToMonths(offer);

    const {
        monthlyRepayment,
        interestRate,
        loadingDrawDownRepaymentScheduleIwocapay
    } = useDrawDownRepaymentScheduleIwocaPay({
        offer,
        payLink
    });

    if (loadingDrawDownRepaymentScheduleIwocapay) {
        return <Skeleton />;
    }

    if (monthlyRepayment === undefined || interestRate === undefined)
        return null;

    const is30Days = is30DaysOffer(offer);

    return (
        <Radio
            {...register(PAYMENT_OPTION_INPUT_ID)}
            id={id}
            checked={isChecked}
            value={id}
            label={
                <label className={styles.label} htmlFor={id}>
                    <span>
                        {is30Days ? (
                            <>
                                <b> Pay {displayCurrency(monthlyRepayment)} </b>{' '}
                                in 30 days time
                            </>
                        ) : (
                            <>
                                <b> Pay {displayCurrency(monthlyRepayment)} </b>{' '}
                                per month for {duration} months
                            </>
                        )}
                    </span>
                    <span className={styles.seperator}>|</span>
                    <span className={styles.interest}>
                        {interestRate === 0
                            ? 'interest-free'
                            : `${(interestRate * 100).toFixed(2)}% per month`}
                    </span>
                </label>
            }
            frame
        />
    );
};
